import type { IButtonStyles } from '@fluentui/react';
import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import { createAppButton, getKeytipString, constructKeytip } from 'owa-command-ribbon';
import type RibbonViewState from 'owa-editor-ribbonplugin-store/lib/store/schema/RibbonViewState';
import type EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import { callEditorApi } from 'owa-editor/lib/utils/callEditorApi';
import { getComposeRibbonId } from 'owa-editor/lib/utils/getComposeRibbonId';
import ClipboardPaste from 'owa-fluent-icons-svg/lib/icons/ClipboardPasteRegular';
import loc from 'owa-localize';
import { FormatControlId } from 'owa-ribbon-ids';
import { extractClipboardItems } from 'roosterjs-editor-dom';
import { ContentTypePrefix } from 'roosterjs-editor-types';
import createWrapper from '../util/controlOnExecuteHandler';
import { pasteText } from './formatControlStrings.locstring.json';
import { isFeatureEnabled } from 'owa-feature-flags';

interface ClipboardItem {
    types: string[];
    getType: (type: string) => Promise<Blob>;
}

interface ChromeClipboard extends Clipboard {
    read: () => Promise<ClipboardItem[]>;
}

function isChromeClipboard(clipboard: Clipboard): clipboard is ChromeClipboard {
    return !!(<ChromeClipboard>clipboard)?.read;
}

function createDataTransferItem(
    kind: 'string' | 'file',
    type: string,
    blob: Blob
): DataTransferItem {
    const file = <any>blob;
    file.name = 'paste.png';
    file.lastModified = new Date();
    return {
        kind,
        type,
        getAsFile: () => file as File,
        getAsString: (callback: FunctionStringCallback) => {
            blob.text().then(text => callback(text));
        },
        webkitGetAsEntry: () => null,
    };
}

export const pasteOperation = async (viewState: EditorViewState) => {
    try {
        callEditorApi(viewState, 'focus');
        const doc = callEditorApi(viewState!, 'getDocument');
        const clipboard = doc.defaultView?.navigator.clipboard;
        if (!clipboard || !isChromeClipboard(clipboard)) {
            return;
        }

        const clipboardDataList = await clipboard.read();
        const promises: Promise<DataTransferItem>[] = [];

        clipboardDataList?.forEach(item => {
            item?.types?.forEach(type => {
                const isText = type?.indexOf(ContentTypePrefix.Text) == 0;
                const isFile = type?.indexOf(ContentTypePrefix.Image) == 0;

                if (isText || isFile) {
                    promises.push(
                        item
                            .getType(type)
                            .then(blob =>
                                createDataTransferItem(isText ? 'string' : 'file', type, blob)
                            )
                    );
                }
            });
        });

        const values = await Promise.all(promises);
        const clipboardData = await extractClipboardItems(values);

        callEditorApi(viewState, 'paste', clipboardData);
    } catch {}
};

export const getPasteControl = computedFn(
    (
        editorViewState: EditorViewState | undefined,
        ribbonViewState: RibbonViewState | undefined,
        styles: IButtonStyles | undefined,
        tabId: number | undefined
    ): RibbonControlProps => {
        return createAppButton(
            getComposeRibbonId(FormatControlId.Paste, editorViewState),
            loc(pasteText),
            createWrapper(editorViewState, ribbonViewState, async () => {
                await pasteOperation(editorViewState!);
            }),
            tabId && isFeatureEnabled('mon-ribbon-keytips')
                ? constructKeytip([getKeytipString(tabId)], getKeytipString(FormatControlId.Paste))
                : undefined,
            ClipboardPaste,
            undefined /* iconColor */,
            styles
        );
    }
);
